import { create } from 'zustand';
import { getMeTenantInvoices } from '../api/generated/me-tenant-invoices/me-tenant-invoices';
import { getTenants } from '../api/generated/tenants/tenants';
import { getPublicPlans } from '../api/generated/public-plans/public-plans';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import { downloadMyTenantInvoicePdf } from '@/utils/downloadReport';
import i18n from '@/i18n/config';
import type {
  FacilityFlowCoreModulesTenantsDtosTenantDto,
  FacilityFlowCoreModulesSubscriptionPlansDtosPublicSubscriptionPlanDto,
} from '../api/generated/models';
import type { TenantInvoiceDto } from '../api/types/admin';

type TenantDto = FacilityFlowCoreModulesTenantsDtosTenantDto;
type PublicPlanDto = FacilityFlowCoreModulesSubscriptionPlansDtosPublicSubscriptionPlanDto;

const { getApiV1TenantsCurrent } = getTenants();
const { getApiV1PublicSubscriptionPlans } = getPublicPlans();
const {
  getApiV1MeTenantBillingInvoices,
  getApiV1MeTenantBillingInvoicesId,
} = getMeTenantInvoices();

const t = (key: string) => i18n.t(key);

interface MySubscriptionState {
  tenant: TenantDto | null;
  plan: PublicPlanDto | null;
  invoices: TenantInvoiceDto[];
  loading: boolean;
  downloadingPdf: boolean;
  error: string | null;

  fetchTenant: () => Promise<void>;
  fetchPlan: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  getInvoiceById: (id: string) => Promise<TenantInvoiceDto | null>;
  downloadPdf: (id: string, invoiceNumber?: string | null) => Promise<void>;
}

export const useMySubscriptionStore = create<MySubscriptionState>((set, get) => ({
  tenant: null,
  plan: null,
  invoices: [],
  loading: false,
  downloadingPdf: false,
  error: null,

  fetchTenant: async () => {
    set({ loading: true, error: null });
    try {
      const tenant = await getApiV1TenantsCurrent();
      set({ tenant: tenant ?? null, loading: false });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  fetchPlan: async () => {
    try {
      const plans = await getApiV1PublicSubscriptionPlans();
      const tenant = get().tenant;
      if (!tenant) {
        set({ plan: null });
        return;
      }
      const match = (plans ?? []).find((p) => p.businessPlan === tenant.businessPlan);
      set({ plan: match ?? null });
    } catch (error) {
      showApiError(error);
    }
  },

  fetchInvoices: async () => {
    try {
      const response = await getApiV1MeTenantBillingInvoices();
      const sorted = [...(response ?? [])].sort((a, b) => {
        const da = a.periodStart ? new Date(a.periodStart).getTime() : 0;
        const db = b.periodStart ? new Date(b.periodStart).getTime() : 0;
        return db - da;
      });
      set({ invoices: sorted });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code });
    }
  },

  getInvoiceById: async (id) => {
    try {
      return await getApiV1MeTenantBillingInvoicesId(id);
    } catch (error) {
      showApiError(error);
      return null;
    }
  },

  downloadPdf: async (id, invoiceNumber) => {
    set({ downloadingPdf: true });
    try {
      await downloadMyTenantInvoicePdf(id, invoiceNumber);
      useToastStore.getState().addToast(t('subscription:invoices.pdfDownloaded'), 'success');
    } catch (error) {
      showApiError(error);
    } finally {
      set({ downloadingPdf: false });
    }
  },
}));
